import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  HardHat,
  Hotel,
  HeartPulse,
  Truck,
  Hammer,
  ShieldCheck,
} from "lucide-react";
import { useMotionPreferences } from "../hooks/use-motion-preferences";
import "./HeroF.css";

/* ────────────────────────────────────────────────────────────────
   Hero F — Editorial Corporate, centered statement + capability grid.
   Russell Reynolds / Spencer Stuart pattern: one confident centered
   H1, then a 6-cell question grid that doubles as nav for the most
   important entry points. No photo. Maximum restraint.
   ──────────────────────────────────────────────────────────────── */

const CAPABILITIES = [
  {
    icon: HardHat,
    question: "Hiring crews for a Gulf project?",
    cta: "Construction & infrastructure",
    href: "/industries/construction-infrastructure",
  },
  {
    icon: Hotel,
    question: "Need hospitality teams at scale?",
    cta: "Hospitality & catering",
    href: "/industries/hospitality-catering",
  },
  {
    icon: HeartPulse,
    question: "Looking for healthcare talent?",
    cta: "Healthcare & caregiving",
    href: "/industries/healthcare-caregiving",
  },
  {
    icon: Truck,
    question: "Filling logistics & warehousing roles?",
    cta: "Logistics & warehousing",
    href: "/industries/logistics-warehousing",
  },
  {
    icon: Hammer,
    question: "Building a manufacturing line?",
    cta: "Manufacturing & industrial",
    href: "/industries/manufacturing-industrial",
  },
  {
    icon: ShieldCheck,
    question: "Need trained security personnel?",
    cta: "Security services",
    href: "/industries/security-services",
  },
];

function WordReveal({ children, delay = 0 }) {
  const words = String(children).split(" ");
  return (
    <span style={{ display: "inline" }}>
      {words.map((w, i) => (
        <span key={i} className="hero-f__word-clip">
          <motion.span
            className="hero-f__word"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{
              duration: 0.7,
              delay: delay + i * 0.07,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            {w}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

export default function HeroF() {
  const { prefersReducedMotion } = useMotionPreferences();
  const ease = [0.22, 1, 0.36, 1];

  return (
    <section className="hero-f">
      <div className="hero-f__container">
        <div className="hero-f__head">
          <motion.span
            className="hero-f__eyebrow"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05, ease }}
          >
            Kenya &rarr; the Gulf &middot; Since 2015
          </motion.span>

          <h1 className="hero-f__title">
            <span className="hero-f__title-line">
              <WordReveal delay={0.18}>Connecting Talents.</WordReveal>
            </span>
            <span className="hero-f__title-line">
              <WordReveal delay={0.40}>Creating</WordReveal>{" "}
              <span className="hero-f__word-clip">
                <motion.span
                  className="hero-f__word hero-f__word--accent"
                  initial={prefersReducedMotion ? false : { y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.8, delay: 0.55, ease }}
                >
                  Opportunities.
                </motion.span>
              </span>
            </span>
          </h1>

          <motion.p
            className="hero-f__sub"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95, ease }}
          >
            Helping employers find the right talent and candidates the right
            job&mdash;every step of recruitment.
          </motion.p>

          <motion.div
            className="hero-f__ctas"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.1, ease }}
          >
            <Link to="/contact" className="hero-f__cta hero-f__cta--primary">
              Hire talent <ArrowRight size={16} strokeWidth={2} />
            </Link>
            <Link to="/job-seekers" className="hero-f__cta hero-f__cta--ghost">
              Find work <ArrowUpRight size={16} strokeWidth={2} />
            </Link>
          </motion.div>
        </div>

        <motion.div
          className="hero-f__capabilities"
          initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.3, ease }}
        >
          {CAPABILITIES.map((c, i) => {
            const Icon = c.icon;
            return (
              <Link key={c.href} to={c.href} className="hero-f__cap">
                <span className="hero-f__cap-icon" aria-hidden="true">
                  <Icon size={20} strokeWidth={1.5} />
                </span>
                <span className="hero-f__cap-question">{c.question}</span>
                <span className="hero-f__cap-cta">
                  {c.cta} <ArrowUpRight size={14} strokeWidth={2} />
                </span>
              </Link>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
